import { ContractorGroupData } from "../ContractorGroup/ContractorGroupData";
import { Timestamp } from "firebase/firestore";
import { FirebaseData } from "../General/FirebaseData";
import { FirebaseFile } from "../General/FirebaseFile";
import { LocalData } from "../General/LocalData";
import { PropertyData } from "../Property/PropertyData";
import { UnitData } from "../Property/Units/UnitData";
import { UserData } from "../User/UserData";
import { DeviationData } from "./DeviationData";
import { DeviationPriority } from "./DeviationPriority";
import { DeviationStatus } from "./DeviationStatus";
import { SeenByMember } from "./SeenByMember";
import { DeviationVisibility, DeviationVisibilityHelper } from "./DeviationVisibility";
import { RelativeData } from "../General/RelativeData";
import { t } from "i18next";

import { Utilities } from "../../../utils/utilities";
import { InvoicedToData } from "./DeviationCostData";
import { ReadAccess } from "../General/AccessData";
import { TaskType } from "./DeviationType";
import { formatTagName } from "../../../data-hooks/company/tags/tags.utils";

export class DeviationUpdateData extends FirebaseData {
  user?: UserData;
  status?: DeviationStatus;
  priority?: DeviationPriority;
  date?: Date;
  comment = "";
  images: FirebaseFile[] = [];
  files: FirebaseFile[] = [];
  tags?: string[];
  contractorGroups: ContractorGroupData[] = [];
  removedContractorGroups: ContractorGroupData[] = [];
  addedResponsibles: UserData[] = [];
  removedResponsibles: UserData[] = [];
  addedExecutors: UserData[] = [];
  removedExecutors: UserData[] = [];
  readAccess: ReadAccess = {};
  seenBy: SeenByMember[] = [];
  archived?: boolean;
  units?: UnitData[];
  floors?: number[];
  placeDescription?: string;
  startDate?: Date;
  endDate?: Date;
  updatedVisibility?: DeviationVisibility;
  newTitle?: string;
  oldTitle?: string;
  newDescription?: string;
  oldDescription?: string;
  addedRelatives: RelativeData[] = [];
  removedRelatives: RelativeData[] = [];
  invoicedTo?: InvoicedToData;
  newType?: TaskType;
  newTaskTypeID?: string;

  // EKSTERN LINK
  senderName?: string;
  senderRole?: string;
  senderEmail?: string;
  senderPhone?: string;

  setup(data: any) {
    if (this.parent instanceof DeviationData && this.parent.parent instanceof PropertyData) {
      const property = this.parent.parent;

      this.units = Array.isArray(data.units) ? data.units.map((unit: string) => property.getUnitWithId(unit)) : undefined;
      this.user = typeof data.userID === "string" ? LocalData.users.addData(data.userID) : undefined;
      this.status = typeof data.status === "number" ? DeviationStatus.getStatusWithId(data.status) : undefined;
      this.priority = typeof data.priority === "number" ? DeviationPriority.getPriorityWithId(data.priority) : undefined;
      this.date = data.date instanceof Timestamp ? data.date.toDate() : undefined;
      this.comment = typeof data.comment === "string" ? data.comment : undefined;
      this.archived = typeof data.archived === "boolean" ? data.archived : undefined;
      this.tags = Array.isArray(data.tags) ? data.tags.filter(Boolean) : [];
      this.floors = Array.isArray(data.floors) ? data.floors.filter((floor: any) => typeof floor === "number") : undefined;
      this.placeDescription = typeof data.placeDescription === "string" ? data.placeDescription : undefined;
      this.readAccess = Utilities.getReadAccess(data.readAccess);
      this.startDate = data.startDate instanceof Timestamp ? data.startDate.toDate() : undefined;
      this.endDate = data.deadline instanceof Timestamp ? data.deadline.toDate() : undefined;
      this.newTitle = typeof data.newTitle === "string" ? data.newTitle : undefined;
      this.oldTitle = typeof data.oldTitle === "string" ? data.oldTitle : undefined;
      this.newDescription = typeof data.newDescription === "string" ? data.newDescription : undefined;
      this.oldDescription = typeof data.oldDescription === "string" ? data.oldDescription : undefined;
      this.newType = typeof data.newType === "string" ? data.newType : undefined;
      this.newTaskTypeID = typeof data.newTaskTypeID === "string" ? data.newTaskTypeID : undefined;

      this.contractorGroups = Array.isArray(data.contractorGroups)
        ? data.contractorGroups.map((id: string) => LocalData.contractorGroups.addData(id))
        : [];
      this.removedContractorGroups = Array.isArray(data.removedContractorGroups)
        ? data.removedContractorGroups.map((id: string) => LocalData.contractorGroups.addData(id))
        : [];
      this.addedResponsibles = Array.isArray(data.addedResponsibles) ? data.addedResponsibles.map((id: string) => LocalData.users.addData(id)) : [];
      this.removedResponsibles = Array.isArray(data.removedResponsibles)
        ? data.removedResponsibles.map((id: string) => LocalData.users.addData(id))
        : [];
      this.addedExecutors = Array.isArray(data.addedExecutors) ? data.addedExecutors.map((id: string) => LocalData.users.addData(id)) : [];
      this.removedExecutors = Array.isArray(data.removedExecutors) ? data.removedExecutors.map((id: string) => LocalData.users.addData(id)) : [];

      if (
        data.invoicedTo !== null &&
        typeof data.invoicedTo === "object" &&
        typeof data.invoicedTo.companyID === "string" &&
        typeof data.invoicedTo.invoiceReceiverID === "string"
      ) {
        this.invoicedTo = {
          company: LocalData.companies.addData(data.invoicedTo.companyID),
          invoiceReceiver: LocalData.companies.addData(data.invoicedTo.companyID).invoiceReceivers.addData(data.invoicedTo.invoiceReceiverID),
        };
      }

      this.senderName = typeof data.senderName === "string" ? data.senderName : undefined;
      this.senderRole = typeof data.senderRole === "string" ? data.senderRole : undefined;
      this.senderEmail = typeof data.senderEmail === "string" ? data.senderEmail : undefined;
      this.senderPhone = typeof data.senderPhone === "string" ? data.senderPhone : undefined;

      this.addedRelatives = [];
      if (typeof data.addedRelatives === "object") {
        for (const relativeID of Object.keys(data.addedRelatives)) {
          this.addedRelatives.push(new RelativeData(relativeID, data.addedRelatives[relativeID]));
        }
      }
      this.removedRelatives = [];
      if (typeof data.removedRelatives === "object") {
        for (const relativeID of Object.keys(data.removedRelatives)) {
          this.removedRelatives.push(new RelativeData(relativeID, data.removedRelatives[relativeID]));
        }
      }

      this.files = [];
      this.images = [];
      if (typeof data.files === "object") {
        for (const fileId in data.files) {
          const file = FirebaseFile.initialize(fileId, data.files[fileId]);
          file.setCreatedBy(this.createdBy, this.user, this.date);

          if (file.isImage()) {
            this.images.push(file);
          } else {
            this.files.push(file);
          }
        }
      }

      this.updatedVisibility = undefined;
      if (this.isValidObject(data.updatedVisibility)) {
        this.updatedVisibility = {
          tenants: data.updatedVisibility.tenants ?? false,
          landlords: data.updatedVisibility.landlords ?? false,
          allTenants: data.updatedVisibility.allTenants ?? false,
        };
      }

      this.seenBy = [];
      if (this.isValidObject(data.seenBy)) {
        for (const userId in data.seenBy) {
          const userData = data.seenBy[userId];
          //  if (userId !== this.user?.id && userId !== LocalData.currentUser.id && userData.date instanceof Timestamp) {
          this.seenBy.push({
            user: LocalData.users.addData(userId),
            date: userData.date.toDate(),
          });
          //  }
        }
      }
    }
    super.setup(data);
  }

  getUpdateContent(): string[] {
    const property = this.parent instanceof DeviationData && this.parent.parent instanceof PropertyData ? this.parent.parent : undefined;
    const isLandlord = property ? property.checkForLandlordRoles(LocalData.currentUser) : false;
    const isEntrepreneur = property ? property.checkForEntrepreneurRoles(LocalData.currentUser) : false;
    const content: string[] = [];
    if (this.senderEmail) content.push(`${t("main.email")}: ${this.senderEmail}`);
    if (this.senderPhone) content.push(`${t("main.phone")}: ${this.senderPhone}`);
    if (this.senderRole) content.push(`${t("main.role")}: ${this.senderRole}`);
    if (this.status) content.push(`${t("main.changedStatusTo")}: ${this.status.name}`, this.status.color);
    if (this.priority && (isLandlord || isEntrepreneur)) content.push(`${t("main.changedPriorityTo")}: ${this.priority.name}`, this.priority.color);
    if (this.tags && (isLandlord || isEntrepreneur))
      content.push(
        `${t("main.changedTagTo")}: ${this.tags
          .map((tag) => LocalData.getTagWithId(tag))
          .map((tag) => (tag ? formatTagName(tag) : ""))
          .join(" ")}`
      );
    if (this.units)
      content.push(`${t("main.updatedUnitsTo")}: ${this.units.length > 0 ? this.units.map((unit) => unit.name).join(", ") : t("main.unspecified")}`);
    if (this.floors)
      content.push(
        `${t("main.updatedFloorsTo")}: ${this.floors.length > 0 ? Utilities.generateStringFromNumberArray(this.floors) : t("main.unspecified")}`
      );
    if (this.placeDescription !== undefined) content.push(`${t("main.updatedPlaceDescriptionTo")}: ${this.placeDescription}`);
    if (this.startDate && (isLandlord || isEntrepreneur))
      content.push(`${t("main.updatedStartDateTo")}: ${Utilities.formatDate(this.startDate, "dd/mmmm/yyyy")}`);
    if (this.startDate === null && (isLandlord || isEntrepreneur)) content.push(t("main.removedStartDate"));
    if (this.endDate && (isLandlord || isEntrepreneur))
      content.push(`${t("main.updatedEndDateTo")}: ${Utilities.formatDate(this.endDate, "dd/mmmm/yyyy")}`);
    if (this.endDate === null && (isLandlord || isEntrepreneur)) content.push(t("main.removedDeadline"));
    if (this.updatedVisibility && (isLandlord || isEntrepreneur))
      content.push(
        `${t("main.updatedVisibilityTo")}: ${DeviationVisibilityHelper.getLabelForVisbility(
          this.parent instanceof DeviationData ? this.parent.units : [],
          this.updatedVisibility
        )}`
      );
    if (this.newTitle) content.push(`${t("main.updatedTitleTo")}: ${this.newTitle}`);
    if (this.oldTitle) content.push(`${t("main.oldTitle")}: ${this.oldTitle}`);
    if (this.newDescription) content.push(`${t("main.updatedDescriptionTo")}: ${this.newDescription}`);
    if (this.oldDescription) content.push(`${t("main.oldDescription")}: ${this.oldDescription}`);
    if (this.comment) content.push(`${t("main.comment")}: ${this.comment}`);
    if (this.contractorGroups.length > 0)
      content.push(`${t("main.assignedContractors")}: ${this.contractorGroups.map((group) => group.name).join(", ")}`);
    if (this.removedContractorGroups.length > 0)
      content.push(`${t("main.removedContractors")}: ${this.removedContractorGroups.map((group) => group.name).join(", ")}`);

    if (this.removedResponsibles.length > 0)
      content.push(`${t("main.removedResponsibles")}: ${this.removedResponsibles.map((responsible) => responsible.name).join(", ")}`);
    if (this.addedResponsibles.length > 0)
      content.push(`${t("main.addedResponsibles")}: ${this.addedResponsibles.map((responsible) => responsible.name).join(", ")}`);
    if (this.removedExecutors.length > 0)
      content.push(`${t("main.removedExecutors")}: ${this.removedExecutors.map((executor) => executor.name).join(", ")}`);
    if (this.addedExecutors.length > 0)
      content.push(`${t("main.addedExecutors")}: ${this.addedExecutors.map((executor) => executor.name).join(", ")}`);

    if (this.addedRelatives.length > 0)
      content.push(`${t("main.addedRelatives")}: ${this.addedRelatives.map((relative) => relative.name).join(", ")}`);
    if (this.removedRelatives.length > 0)
      content.push(`${t("main.removedRelatives")}: ${this.removedRelatives.map((relative) => relative.name).join(", ")}`);

    if (this.archived) content.push(this.archived ? t("main.archivedTask") : t("main.unarchivedTask"));

    return content;
  }

  saveSeenBy(completion?: (error?: Error) => void) {
    if (this.seenBy.find((data) => data.user === LocalData.currentUser)) return;
    const data: { [key: string]: any } = {};
    data[`seenBy.${LocalData.currentUser.id}.date`] = new Date();

    super.saveUpdateWithCompletionHandler(data, (error) => {
      if (completion) completion(error);
    });
  }

  saveImage(index: number, tries: number) {
    if (tries > 3) return;
    this.images[index].saveAndLoadURL((success) => {
      if (!success) {
        this.saveImage(index, tries + 1);
      }
    });
  }

  saveFile(index: number, tries: number) {
    if (tries > 3) return;
    this.files[index].saveAndLoadURL((success) => {
      if (!success) {
        this.saveFile(index, tries + 1);
      }
    });
  }

  setGlobalFileID(file: FirebaseFile, globalFileID: string) {
    const data: { [key: string]: any } = {};
    data[`files.${file.id}.globalFileID`] = globalFileID;
    // The empty then() is so a Promise<void> is returned for error handling
    return super.saveUpdate(data).then();
  }
}

